import React, { useState, useCallback, Fragment } from 'react';
import styled from 'styled-components';
import { Typo, Input, Form, Dropdown, Icon as _Icon } from 'tmt-ui-kit';
import Link from '~/components/Link/Link';
import PropTypes from 'prop-types';
import { countries } from '~/utils/countries';

const TitleForm = styled(Typo.HeaderTwo)`
  border-left: 5px solid ${props => props.theme.greenLighter};
  padding: 8px 24px;
`;

const Padding = styled.div`
  padding: 24px 50px;
  ${props => props.isPaddingBottom && 'padding-bottom: 0px;'}
  p { 
    text-align: right;
  }

  @media(max-width: 600px) {
    padding: 16px 24px;
  }
`;

const Label = styled(Typo.HeaderFour)`
  ${props => props.margin && `margin: ${props.margin};`}
`;

const SubLabel = styled(Typo.Paragraph)`
  text-align: left !important;
`;

const Important = styled.span`
  color: ${props => props.theme.red};
  margin-left: 4px;
`;

const MarginBottom = styled.div`
  margin-bottom: 18px;
`;

const CheckBox = styled(Form.CheckBox)`
  margin: 36px 0;
`;

const Text = styled(Typo.Paragraph)`
  font-size: 16px !important;
`;

const LabelCheckbox = styled(Text)`
  margin: 0px;
  text-align: left !important;
`;

const TextBlue = styled.span`
  color: ${props => props.theme.blue} !important;
`;

const CountryDropdown = styled(Dropdown.Dropdown)`
  border: 1px solid ${props => (props.error ? props.theme.red : props.theme.lightGray1)};
  background: ${props => props.theme.white};
  margin: 8px 0px;

  >p {
    color: ${props => props.isChangeColor && `${props.theme.lightGray} !important;`};
    text-align: left;
  }
`;

const UploadPhotoContainer = styled.form`
  border-radius: 4px;
  width: 150px;
  padding: 10px 14px 10px 20px;
  position: relative;
  display: flex;
  align-items: center;
  height: auto;
  margin-bottom: 20px;
  border: 1px solid ${props => props.theme.orange};
  background: ${props => props.theme.white};
  cursor: pointer;

  ${props => (props.isImage ? `
    background: ${props.theme.white};
    width: fit-content;
    padding: 10px 20px;
    border: 1px solid ${props.theme.pink};
  ` : `
    background: linear-gradient(to bottom, ${props.theme.orange}, ${props.theme.pink});
  `)}

  @media(max-width: 600px) {
    width: 150px;
  }
`;

const UploadPhoto = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  height: 100%;
  width: 100%;
  opacity: 0;
  ${props => !props.isImage && 'cursor: pointer;'}
`;

const TextUploadPhoto = styled.label`
  cursor: pointer;
  display: block;
  font-size: 16px;
  font-weight: 500;
  color: ${props => props.theme.orange};

  @media(max-width: 600px) {
    width: 167px;
  }
  @media(max-width: 370px) {
    font-size: 14px;
  }
`;

const IconShare = styled(_Icon)`
  transform: rotate(-180deg);
  color: ${props => props.theme.orange};
  margin-right: 8px;
  margin-left: -8px;
`;

const ImageContainer = styled.div`
  position: relative;
  max-width: fit-content;
  margin: 0 auto;
  padding: 10px;
  border-radius: 10px;
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const IconClose = styled(_Icon)`
  position: absolute;
  cursor: pointer;
  top: 12px;
  right: 12px;
  color: ${props => props.theme.white};
`;

const Image = styled.img`
  max-width: 100%;
  border-radius: 4px;
`;

const StepTwoForm = (props) => {
  const { signUpForm } = props;
  const [image, setImage] = useState(undefined);

  const handleUploadFile = useCallback((e) => {
    const { files } = e.target;
    if (!files) return;
    const file = files[0];
    signUpForm.change('fileUpload', file);
    const reader = new window.FileReader();
    reader.readAsDataURL(file);
    reader.onload = (data) => {
      setImage(data.target.result);
    };
  });

  const handleRemoveImage = useCallback(() => {
    setImage(undefined);
    signUpForm.change('fileUpload', undefined);
  });

  return (
    <Fragment>
      <TitleForm>Website Info</TitleForm>
      <Padding isPaddingBottom>
        <Label>Website URL<Important>*</Important></Label>
        <MarginBottom>
          <Input.Input
            type="text"
            name="websiteUrl"
            value={signUpForm.values.websiteUrl}
            handleOnChange={(e) => signUpForm.change('websiteUrl', e.target.value)}
            errorMessage={signUpForm.errors.websiteUrl}
          />
        </MarginBottom>
        <Label>Country of target audience<Important>*</Important></Label>
        <MarginBottom>
          <CountryDropdown
            options={countries}
            onChange={(value) => signUpForm.change('targetCountry', value)}
            selected={signUpForm.values.targetCountry}
            isChangeColor={!signUpForm.values.targetCountry}
            error={signUpForm.errors.targetCountry}
          />
        </MarginBottom>
        <Label>Website Audience Overview</Label>
        <SubLabel>Please share your Google Analytics screenshot on <br/>&quot;Audience {'->'} Overview&quot; page for the last 30 days</SubLabel>
        <MarginBottom>
          <UploadPhotoContainer>
            <IconShare name="share"/>
            <TextUploadPhoto>
              {image ? 'Upload Successful' : 'Add File'}
            </TextUploadPhoto>
            <UploadPhoto accept="image/*" type="file" onChange={handleUploadFile} />
          </UploadPhotoContainer>
          {image && (
            <ImageContainer>
              <IconClose name="close" onClick={handleRemoveImage} />
              <Image src={image} alt="upload file" />
            </ImageContainer>
          )}
        </MarginBottom>
        <Label>Facebook URL</Label>
        <MarginBottom>
          <Input.Input
            type="text"
            name="feacbookUrl"
            value={signUpForm.values.feacbookUrl}
            handleOnChange={(e) => signUpForm.change('feacbookUrl', e.target.value)}
          />
        </MarginBottom>
        <Label>Instagram URL</Label>
        <MarginBottom>
          <Input.Input
            type="text"
            name="instagramUrl"
            value={signUpForm.values.instagramUrl}
            handleOnChange={(e) => signUpForm.change('instagramUrl', e.target.value)}
          />
        </MarginBottom>
        <Label>Twitter URL</Label>
        <MarginBottom>
          <Input.Input
            type="text"
            name="twitterUrl"
            value={signUpForm.values.twitterUrl}
            handleOnChange={(e) => signUpForm.change('twitterUrl', e.target.value)}
          />
        </MarginBottom>
        <Label>YouTube URL</Label>
        <MarginBottom>
          <Input.Input
            type="text"
            name="youtubeUrl"
            value={signUpForm.values.youtubeUrl}
            handleOnChange={(e) => signUpForm.change('youtubeUrl', e.target.value)}
          />
        </MarginBottom>
        <Label>What would you provide us in return?<Important>*</Important></Label>
        <MarginBottom>
          <Input.Input
            type="text"
            name="providence"
            value={signUpForm.values.providence}
            handleOnChange={(e) => signUpForm.change('providence', e.target.value)}
            errorMessage={signUpForm.errors.providence}
          />
        </MarginBottom>
        <Label>Other Message</Label>
        <MarginBottom>
          <Input.Input
            type="text"
            name="otherMessage"
            value={signUpForm.values.otherMessage}
            handleOnChange={(e) => signUpForm.change('otherMessage', e.target.value)}
          />
        </MarginBottom>
        <CheckBox
          checked={signUpForm.values.isGdpr}
          onChange={() => signUpForm.change('isGdpr', !signUpForm.values.isGdpr)}
          renderLabel={() => (
            <LabelCheckbox>
              <Label margin="2px 0px 0px">GDPR Consent<Important>*</Important></Label>
              By clicking yes, I confirm that I have read and agree to allow TakeMeTour Pte. Ltd to collect, use and/or process personal data about me that I have previously provided to TakeMeTour in accordance to the GDPR Act.
            </LabelCheckbox>
          )}
        />
        <CheckBox
          checked={signUpForm.values.isAgree}
          onChange={() => signUpForm.change('isAgree', !signUpForm.values.isAgree)}
          renderLabel={() => (
            <LabelCheckbox>
              I agree to the <TextBlue><Link href="https://www.takemetour.com/article/terms-conditions-and-disclaimer" target="_blank" rel="noopener noreferrer">terms and conditions</Link></TextBlue>
            </LabelCheckbox>
          )}
        />
      </Padding>
    </Fragment>
  );
};

StepTwoForm.propTypes = {
  signUpForm: PropTypes.object,
};

export default StepTwoForm;
